import React, { useEffect, useState, useRef } from 'react';
import { 
  Card, 
  Spin, 
  Typography, 
  Tabs, 
  Table, 
  Tag, 
  Button, 
  Space, 
  Descriptions,
  Row,
  Col,
  Divider,
  Alert,
  Modal,
  Select,
  Progress,
  App
} from 'antd';
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  CopyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  RobotOutlined,
  PictureOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { getVideoAuditTaskDetail, getVideoAuditTaskResult, retryAudit, getAiApiConfig, updateTaskDuration, refreshMediaUrl, checkTaskStatus, updateTaskAuditStatus} from '../../services/videoAuditService';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import PptExtractViewer from './PptExtractViewer';
import { formatDuration } from '../../utils/formatUtils';

const { Title, Text, Paragraph } = Typography;

// CSS样式
const styles = {
  chatTimelineContainer: {
    padding: '10px 0',
  },
  chatMessage: {
    marginBottom: '16px',
    padding: '10px 15px',
    borderRadius: '8px',
    background: '#f9f9f9',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  chatMessageCurrent: {
    background: '#e6f7ff',
    borderLeft: '3px solid #1890ff',
  },
  chatMessageHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  chatTime: {
    marginLeft: '10px',
    color: '#888',
    fontSize: '12px',
  },
  chatMessageContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    wordBreak: 'break-word',
  },
  chatGap: {
    marginBottom: '16px',
    padding: '8px 12px',
    borderRadius: '6px',
    background: '#fff2e8',
    borderLeft: '3px solid #fa8c16',
  },
  chatGapContent: {
    display: 'flex',
    alignItems: 'center',
    color: '#fa8c16',
    fontSize: '12px',
  },
  videoControls: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  progressBarContainer: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e6e6e6',
    borderRadius: '4px',
    position: 'relative',
    cursor: 'pointer',
    marginBottom: '8px',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#1890ff',
    borderRadius: '4px',
    transition: 'width 0.1s',
  },
  timeDisplay: {
    fontSize: '14px',
    color: '#595959',
    fontFamily: 'monospace',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  }
};

// 格式化时间
const formatTime = (ms) => {
  if (!ms && ms !== 0) return '--:--:--';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = ms % 1000;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
};

// 格式化时长显示（处理毫秒输入）
const formatDurationFromMs = (ms) => {
  if (!ms && ms !== 0) return '-';
  
  const totalSeconds = Math.floor(ms / 1000);
  return formatDuration(totalSeconds);
};

// 将毫秒转换为时:分:秒格式（无毫秒）
const formatTimeSimple = (ms) => {
  if (!ms && ms !== 0) return '--:--';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// 渲染状态标签
const renderStatusTag = (status) => {
  const statusMap = {
    0: { color: 'default', text: '等待处理', icon: <ClockCircleOutlined /> },
    1: { color: 'processing', text: '处理中', icon: <ClockCircleOutlined /> },
    2: { color: 'success', text: '已完成', icon: <CheckCircleOutlined /> },
    3: { color: 'error', text: '失败', icon: <CloseCircleOutlined /> }
  };
  
  const { color, text, icon } = statusMap[status] || { color: 'default', text: '未知状态', icon: <InfoCircleOutlined /> };
  
  return <Tag color={color} icon={icon}>{text}</Tag>;
};

const TaskDetail = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { refreshUserInfo } = useAuth();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [taskInfo, setTaskInfo] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [activeTab, setActiveTab] = useState('translation');
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rawTranscriptionData, setRawTranscriptionData] = useState(null);
  const [transcriptionUrl, setTranscriptionUrl] = useState('');
  const [fetchingTranscription, setFetchingTranscription] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [aiCheckLoading, setAiCheckLoading] = useState(false);
  const [aiCheckResult, setAiCheckResult] = useState(null);
  const [selectedModel, setSelectedModel] = useState('THUDM/glm-4-9b-chat');
  const [modelSelectVisible, setModelSelectVisible] = useState(false);
  const [rawApiResponse, setRawApiResponse] = useState(null);
  const [fullResponseText, setFullResponseText] = useState("");
  const [truncationInfo, setTruncationInfo] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, percent: 0 });
  
  // 可用的模型列表
  const availableModels = [
    { value: 'Pro/deepseek-ai/DeepSeek-V3', label: 'DeepSeek-V3 (Pro)' },
    { value: 'THUDM/glm-4-9b-chat', label: '智普GLM4-9B-128K' },
    { value: 'Qwen/Qwen2.5-72B-Instruct-128K', label: '通义千问Qwen2.5-72B-Instruct-128K' },
    { value: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B', label: 'DeepSeek-R1-Distill-Qwen-7B-32K' }
  ];

  // 获取任务详情
  const fetchTaskDetail = async () => {
    try {
      setLoading(true);
      const response = await getVideoAuditTaskDetail(taskId);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据，处理{task: {...}}格式
      const taskData = responseData.task || responseData;
      
      if (taskData && taskData.id) {
        // 解析result_data字段
        if (taskData.result_data) {
          try {
            const resultData = JSON.parse(taskData.result_data);
            if (resultData.Result && resultData.Result.Transcription) {
              taskData.transcription_url = resultData.Result.Transcription;
            }
          } catch (e) {
            console.error('解析result_data失败:', e);
          }
        }

        setTaskInfo(taskData);

        // 如果任务已完成，获取详细结果
        if (taskData.status === 2) {
          try {
            const resultResponse = await getVideoAuditTaskResult(taskId);
            
            // 统一处理响应格式
            const resultData = resultResponse.data || resultResponse;
            
            if (resultData) {
              setAuditResult(resultData);
            }
          } catch (resultError) {
            console.error('获取任务结果失败:', resultError);
            message.error('获取任务结果失败，请稍后重试');
          }
        }

        // 如果有转录文本URL，获取转录数据
        if (taskData.transcription_url) {
          setTranscriptionUrl(taskData.transcription_url);
          await fetchTranscriptionData(taskData.transcription_url);
        }
      } else {
        console.error('获取任务详情失败:', responseData);
        message.error('获取任务详情失败，请稍后重试');
      }
    } catch (error) {
      console.error('获取任务详情失败:', error);
      message.error('获取任务详情失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 加载转写文件原始数据
  const fetchTranscriptionData = async (url) => {
    // 使用传入的url或已经设置的transcriptionUrl
    const transcriptUrl = url || transcriptionUrl;
    
    if (!transcriptUrl) {
      return;
    }
    
    setFetchingTranscription(true);
    try {
      const response = await fetch(transcriptUrl);
      if (response.ok) {
        const data = await response.json();
        setRawTranscriptionData(data);
        
        // 检查并更新时长数据到数据库
        updateDurationFromTranscription(data);
      } else {
        message.error('获取转写文件失败: ' + response.statusText);
      }
    } catch (error) {
      console.error('[时长更新日志] 获取转写文件异常:', error);
      message.error('获取转写文件失败');
    } finally {
      setFetchingTranscription(false);
    }
  };

  // 更新时长数据到数据库
  const updateDurationFromTranscription = async (data) => {
    if (!data || !data.Transcription || !data.Transcription.AudioInfo || !data.Transcription.AudioInfo.Duration) {
      return;
    }
    
    // 获取转写文件中的时长数据
    const duration = data.Transcription.AudioInfo.Duration;
                
    // 检查时长是否存在，如果转写数据中有时长，则直接更新到数据库
    if (duration > 0) {
      try {
        // 更新任务时长
        const response = await updateTaskDuration(taskId, duration);
        
        // 统一处理响应格式
        const responseData = response.data || response;
        
        if (responseData && responseData.id) {
          // 更新存档任务信息
          setTaskInfo(prev => {
            const updated = {
              ...prev,
              duration: duration,
              is_duration_added_to_user: responseData.is_duration_added_to_user
            };
            return updated;
          });
          
          // 用户时长的更新现在由后端处理，不再需要前端调用 updateUserVideoDuration
          // 只需要刷新用户信息，更新UI显示
          await refreshUserInfo();
        } else {
          console.error('[时长更新日志] 更新时长数据失败:', responseData);
        }
      } catch (error) {
        console.error('[时长更新日志] 更新时长数据请求失败:', error);
      }
    }
  };

  useEffect(() => {
    fetchTaskDetail();
  }, [taskId]);

  // 处理播放/暂停
  const handlePlayPause = () => {
    const media = taskInfo.is_video ? videoRef.current : audioRef.current;
    
    if (playing) {
      media.pause();
    } else {
      media.play();
    }
    
    setPlaying(!playing);
  };

  // 跳转到特定时间点
  const jumpToTime = (timeInMs) => {
    const media = taskInfo.is_video ? videoRef.current : audioRef.current;
    
    if (media) {
      media.currentTime = timeInMs / 1000;
      if (!playing) {
        media.play();
        setPlaying(true);
      }
    }
  };

  // 监听媒体时间更新
  const handleTimeUpdate = () => {
    const media = taskInfo.is_video ? videoRef.current : audioRef.current;
    if (media) {
      setCurrentTime(media.currentTime * 1000);
    }
  };

  // 监听媒体加载完成获取总时长
  const handleMediaLoaded = () => {
    const media = taskInfo.is_video ? videoRef.current : audioRef.current;
    if (media) {
      setDuration(media.duration * 1000);
    }
  };
  
  // 处理进度条点击
  const handleProgressClick = (e) => {
    const media = taskInfo.is_video ? videoRef.current : audioRef.current;
    if (!media) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const clickPosition = offsetX / rect.width;
    
    const newTime = clickPosition * media.duration;
    media.currentTime = newTime;
    setCurrentTime(newTime * 1000);
  };

  // 复制转写文本
  const copyTranscriptionText = () => {
    if (!auditResult || !auditResult.transcription) return;
    
    const text = auditResult.transcription.map(item => item.text).join(' ');
    
    navigator.clipboard.writeText(text)
      .then(() => {
        message.success('转写文本已复制到剪贴板');
      })
      .catch(err => {
        message.error('复制失败');
        console.error('复制失败:', err);
      });
  };

  // 返回列表页
  const goBack = () => {
    navigate('/video-audit/task-list');
  };

  // 重试审核任务
  const handleRetry = async () => {
    try {
      setLoading(true);
      
      const response = await retryAudit(taskId);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.id && typeof responseData.status !== 'undefined') {
        message.success('审核任务已重新启动');
        fetchTaskDetail();
      } else {
        console.error('重试审核失败:', responseData);
        message.error('重试审核失败');
      }
    } catch (error) {
      console.error('重试审核出错:', error);
      message.error('重试审核失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };
  
  // 刷新任务状态
  const handleRefreshStatus = async () => {
    try {
      setLoading(true);
      
      const response = await checkTaskStatus(taskId);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.id && typeof responseData.status !== 'undefined') {
        message.success('任务状态已刷新');
        fetchTaskDetail();
      } else {
        console.error('刷新任务状态失败:', responseData);
        message.error('刷新任务状态失败');
      }
    } catch (error) {
      console.error('刷新任务状态出错:', error);
      message.error('刷新任务状态失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 处理审核状态更新
  const handleUpdateAuditStatus = async (isReviewed) => {
    try {
      setLoading(true);
      
      const response = await updateTaskAuditStatus(taskId, isReviewed);
      
      // 统一处理响应格式
      const responseData = response.data || response;
      
      // 检查响应数据
      if (responseData && responseData.id && typeof responseData.is_reviewed !== 'undefined') {
        message.success(isReviewed ? '已标记为已审核' : '已撤销审核标记');
        fetchTaskDetail();
      } else {
        console.error('更新审核状态失败:', responseData);
        message.error('更新审核状态失败');
      }
    } catch (error) {
      console.error('更新审核状态出错:', error);
      message.error('更新审核状态失败：' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 调用大模型API检查内容
  const checkContentWithAI = async () => {
    if (!rawTranscriptionData || !rawTranscriptionData.Transcription || !rawTranscriptionData.Transcription.Paragraphs) {
      message.error('无法获取转写数据');
      return;
    }

    // 从后端获取API密钥
    try {
      setAiCheckLoading(true);
      
      // 获取API配置
      const configResponse = await getAiApiConfig();
      
      // 统一处理响应格式
      const configData = configResponse.data || configResponse;
      
      if (!configData || !configData.siliconflow_api_key) {
        Modal.error({
          title: '无法获取API配置',
          content: '系统未配置硅流API密钥，请联系管理员。'
        });
        setAiCheckLoading(false);
        return;
      }
      
      const apiKey = configData.siliconflow_api_key;
      const apiUrl = configData.siliconflow_api_url || 'https://api.siliconflow.cn/v1/chat/completions';
      
      // 提取所有段落文本内容
      const paragraphs = rawTranscriptionData.Transcription.Paragraphs.map(paragraph => {
        const text = paragraph.Words ? paragraph.Words.map(word => word.Text).join('') : '';
        const speakerId = paragraph.SpeakerId;
        const startTime = paragraph.Words && paragraph.Words.length > 0 ? formatTimeSimple(paragraph.Words[0].Start) : '';
        const endTime = paragraph.Words && paragraph.Words.length > 0 ? formatTimeSimple(paragraph.Words[paragraph.Words.length - 1].End) : '';
        return { text, speakerId, startTime, endTime };
      }).filter(p => p.text.trim() !== '');
      
      // 分批处理参数
      const BATCH_SIZE = getModelBatchSize(selectedModel);
      const totalParagraphs = paragraphs.length;
      const batchCount = Math.ceil(totalParagraphs / BATCH_SIZE);
      
      // 如果内容很少，直接一次性处理
      if (batchCount <= 1) {
        await processSingleBatch(paragraphs, apiKey, apiUrl);
        return;
      }
      
      // 分批处理
      setBatchProgress({
        current: 0,
        total: batchCount,
        percent: 0
      });
      
      // 创建分批
      const batches = [];
      for (let i = 0; i < batchCount; i++) {
        const start = i * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, totalParagraphs);
        batches.push(paragraphs.slice(start, end));
      }
      
      // 每批结果
      const batchResults = [];
      
      // 记录总token使用量
      let totalTokenUsage = 0;
      
      // 处理每个批次
      for (let i = 0; i < batches.length; i++) {
        const batchParagraphs = batches[i];
        const startIndex = i * BATCH_SIZE;
        
        setBatchProgress({
          current: i + 1,
          total: batchCount,
          percent: Math.round(((i + 1) / batchCount) * 100)
        });
        
        // 显示当前处理进度
        message.info(`正在处理第 ${i + 1}/${batchCount} 批段落（${startIndex + 1}-${startIndex + batchParagraphs.length}/${totalParagraphs}）`);
        
        try {
          // 构建提示词
          const prompt = createPrompt(batchParagraphs, i, batchCount, startIndex);
          
          // 调用API
          const response = await axios.post(apiUrl, {
            model: selectedModel,
            messages: [
              {
                role: 'user',
                content: prompt
              }
            ],
            temperature: 0.5,
            max_tokens: 2048,
            response_format: { 
              type: "text" 
            }
          }, {
            headers: {
              'Authorization': `Bearer ${apiKey}`,
              'Content-Type': 'application/json'
            }
          });
          
          // 解析API响应
          if (response.data && response.data.choices && response.data.choices.length > 0) {
            try {
              const contentText = response.data.choices[0].message.content;
              
              // 保存原始响应文本
              if (i === 0) {
                setRawApiResponse(response.data);
                setFullResponseText(contentText);
              }
              
              // 累计token使用量
              if (response.data.usage) {
                const batchTokens = response.data.usage.total_tokens || 
                                   (response.data.usage.prompt_tokens + response.data.usage.completion_tokens);
                totalTokenUsage += batchTokens;
              }
              
              // 从文本中提取JSON部分
              const jsonMatch = contentText.match(/\{[\s\S]*\}/);
              if (jsonMatch) {
                const jsonStr = jsonMatch[0];
                const result = JSON.parse(jsonStr);
                
                // 修正段落索引，加上起始偏移
                if (result.timeRelated && Array.isArray(result.timeRelated)) {
                  result.timeRelated.forEach(item => {
                    if (typeof item.paragraphIndex === 'number') {
                      item.paragraphIndex += startIndex;
                    }
                  });
                }
                
                if (result.irrelevantContent && Array.isArray(result.irrelevantContent)) {
                  result.irrelevantContent.forEach(item => {
                    if (typeof item.paragraphIndex === 'number') {
                      item.paragraphIndex += startIndex;
                    }
                  });
                }
                
                batchResults.push({
                  result,
                  contentText,
                  response: response.data
                });
              } else {
                throw new Error('无法从响应中提取JSON');
              }
            } catch (error) {
              console.error('解析AI响应失败:', error);
              message.error(`第 ${i + 1} 批段落处理失败: 解析结果错误`);
            }
          } else {
            throw new Error('API响应格式不正确');
          }
        } catch (error) {
          console.error(`处理第 ${i + 1} 批次失败:`, error);
          message.error(`第 ${i + 1} 批段落处理失败: ${error.message}`);
        }
        
        // 批次间隔，避免频率限制
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // 更新用户token使用量（所有批次总和）
      if (totalTokenUsage > 0) {
        await updateUserTokenUsage(totalTokenUsage);
      }
      
      // 合并所有批次结果
      const combinedResult = combineResults(batchResults);
      
      // 设置最终结果
      setAiCheckResult(combinedResult);
      
      // 设置处理完成
      setTruncationInfo({
        includedParagraphs: totalParagraphs,
        totalParagraphs: totalParagraphs,
        isTruncated: false,
        processedInBatches: batchCount > 1
      });
      
      // 显示完成信息
      message.success(`所有 ${totalParagraphs} 个段落处理完成`);
    } catch (error) {
      console.error('AI内容检查失败:', error);
      message.error('AI内容检查失败');
    } finally {
      setAiCheckLoading(false);
      setBatchProgress({ current: 0, total: 0, percent: 0 });
    }
  };
  
  // 根据模型获取批处理大小
  const getModelBatchSize = (model) => {
    if (model.includes('32B') || model.includes('34B')) {
      return 75; // 大模型支持更多段落
    } else if (model.includes('14B')) {
      return 50; // 中型模型
    } else {
      return 40; // 小模型减少段落数
    }
  };
  
  // 单批次处理
  const processSingleBatch = async (paragraphs, apiKey, apiUrl) => {
    try {
      // 构建提示词
      const prompt = createPrompt(paragraphs, 0, 1, 0);
      
      // 调用API
      const response = await axios.post(apiUrl, {
        model: selectedModel,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.5,
        max_tokens: 2048,
        response_format: { 
          type: "text" 
        }
      }, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });
      
      // 解析API响应
      if (response.data && response.data.choices && response.data.choices.length > 0) {
        try {
          const contentText = response.data.choices[0].message.content;
          
          // 从文本中提取JSON部分
          const jsonMatch = contentText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const jsonStr = jsonMatch[0];
            const result = JSON.parse(jsonStr);
            setAiCheckResult(result);
            setRawApiResponse(response.data);
            setFullResponseText(contentText);
            
            // 设置处理信息
            setTruncationInfo({
              includedParagraphs: paragraphs.length,
              totalParagraphs: paragraphs.length,
              isTruncated: false
            });
            
            // 更新用户token使用量
            if (response.data.usage) {
              const totalTokens = response.data.usage.total_tokens || 
                                 (response.data.usage.prompt_tokens + response.data.usage.completion_tokens);
              updateUserTokenUsage(totalTokens);
            }
          } else {
            throw new Error('无法从响应中提取JSON');
          }
        } catch (error) {
          console.error('解析AI响应失败:', error);
          message.error('解析AI检查结果失败');
        }
      } else {
        throw new Error('API响应格式不正确');
      }
    } catch (error) {
      console.error('AI内容检查失败:', error);
      message.error('AI内容检查失败');
    }
  };
  
  // 创建提示词
  const createPrompt = (paragraphs, batchIndex, totalBatches, startIndex) => {
    const isBatched = totalBatches > 1;
    
    return `
作为教学内容检查专家，请详细分析以下教学视频的转写内容，重点检查两个方面：

1. 时间相关的表述问题：
   - 仅标记明确包含绝对时间的表述，如"昨天"、"上周"、"明天"、"今年"等确切时间指代
   - 不要标记"本节课"、"这一讲"、"今天的内容"等常见的教学表述
   - 不要标记"现在"、"接下来"等相对时间表述
   - 不要标记课程开场白中的"今天我们要学习..."等常见教学引导语

2. 教学无关内容问题：
   - 仅标记出与当前教学主题完全无关的闲聊内容（如与课程内容无关的日常聊天、闲谈等）
   - 不要标记作为教学一部分的题目讲解、提问解答、师生互动内容
   - 不要评价教师的教学表述方式或风格
   - 不要标记口头禅或口语化表达
   - 正常的教学流程中教师讲解问题或回答学生提问不应被标记为无关内容
   - 视频开头的介绍性内容（如水印、片头等）应标记为无关内容

请按照以下JSON格式返回分析结果：
{
  ${batchIndex === 0 ? `"summary": "对整体内容质量的简要评价，100-200字",` : ''}
  "timeRelated": [
    {
      "paragraphIndex": 段落索引（数字）,
      "text": "出现问题的原文",
      "issue": "说明为什么这是一个绝对时间表述问题（例如'昨天'表示特定的一天，在不同时间观看视频时会产生歧义）",
      "suggestion": "修改或改进建议"
    }
  ],
  "irrelevantContent": [
    {
      "paragraphIndex": 段落索引（数字）,
      "text": "出现问题的原文",
      "issue": "说明为什么这段内容与教学主题完全无关（不要将题目讲解、提问解答误判为无关内容）",
      "suggestion": "修改或改进建议"
    }
  ]${batchIndex === totalBatches - 1 ? `,
  "recommendation": "对整体内容的改进建议，200字左右"` : ''}
}

${isBatched ? `注意：这是第 ${batchIndex + 1}/${totalBatches} 批转写内容，对应原始段落索引 ${startIndex + 1} 到 ${startIndex + paragraphs.length}。请确保返回的问题中段落索引是基于原始段落编号（从0开始）。\n\n` : ''}
转写内容：
${paragraphs.map((p, idx) => `[段落${startIndex + idx + 1}, 说话人${p.speakerId}, ${p.startTime}-${p.endTime}] ${p.text}`).join('\n')}
`;
  };
  
  // 合并多个批次的结果
  const combineResults = (batchResults) => {
    if (!batchResults.length) return null;
    
    // 初始化合并结果
    const combinedResult = {
      summary: batchResults[0]?.result?.summary || '',
      timeRelated: [],
      irrelevantContent: [],
      recommendation: batchResults[batchResults.length - 1]?.result?.recommendation || ''
    };
    
    // 合并所有批次的问题
    batchResults.forEach(batch => {
      if (batch.result?.timeRelated && Array.isArray(batch.result.timeRelated)) {
        combinedResult.timeRelated = [...combinedResult.timeRelated, ...batch.result.timeRelated];
      }
      
      if (batch.result?.irrelevantContent && Array.isArray(batch.result.irrelevantContent)) {
        combinedResult.irrelevantContent = [...combinedResult.irrelevantContent, ...batch.result.irrelevantContent];
      }
    });
    
    // 按段落索引排序
    combinedResult.timeRelated.sort((a, b) => a.paragraphIndex - b.paragraphIndex);
    combinedResult.irrelevantContent.sort((a, b) => a.paragraphIndex - b.paragraphIndex);
    
    return combinedResult;
  };
  
  // 更新用户token使用量
  const updateUserTokenUsage = async (tokenCount) => {
    try {
      // 获取认证token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('无法更新token使用量：未找到认证token');
        return;
      }
      
      // 调用后端API更新用户token使用量
      const response = await axios.post('/api/users/update-token-usage', {
        token_count: tokenCount
      }, {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.code === 0) {
        // 刷新用户信息，更新UI上的token使用量数据
        await refreshUserInfo();
      } else {
        console.error('更新token使用量失败:', response.data.message);
      }
    } catch (error) {
      console.error('更新token使用量失败:', error);
    }
  };

  // 显示模型选择对话框
  const showModelSelectModal = () => {
    setModelSelectVisible(true);
  };
  
  // 确认模型选择并开始检查
  const handleModelSelect = () => {
    setModelSelectVisible(false);
    checkContentWithAI();
  };

  // 添加一个useEffect钩子来在auditResult加载后更新activeTab
  useEffect(() => {
    if (auditResult) {
      if (auditResult.translation) {
        setActiveTab('translation');
      } else if (auditResult.vad) {
        setActiveTab('vad');
      }
    }
  }, [auditResult]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin>
          <div style={{ padding: '50px', background: 'rgba(0, 0, 0, 0.05)', borderRadius: '4px', width: '300px', height: '200px' }}>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>加载中...</div>
          </div>
        </Spin>
      </div>
    );
  }

  if (!taskInfo) {
    return (
      <Card>
        <div style={{ textAlign: 'center' }}>
          <Title level={4}>未找到任务信息</Title>
          <Button type="primary" onClick={goBack}>返回列表</Button>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <Text strong style={{ fontSize: '16px' }}>任务ID: {taskId}</Text>
              <Title level={4} style={{ margin: 0 }}>{taskInfo.task_name || "网课审核任务"}</Title>
            </div>
            <Space size="middle" split={<Divider type="vertical" />}>
              {renderStatusTag(taskInfo.status)}
              <Text>{taskInfo.is_video ? '视频' : '音频'}</Text>
              <Text>{new Date(taskInfo.create_time).toLocaleString()}</Text>
              {taskInfo.is_reviewed !== undefined && (
                <Tag color={taskInfo.is_reviewed ? 'success' : 'default'}>
                  {taskInfo.is_reviewed ? '已审核' : '未审核'}
                </Tag>
              )}
            </Space>
          </div>
          <Space>
            <Button onClick={goBack}>返回列表</Button>
            {taskInfo.status === 2 && (
              taskInfo.is_reviewed ? (
                <Button 
                  danger
                  type="primary" 
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleUpdateAuditStatus(false)}
                >
                  撤销审核
                </Button>
              ) : (
                <Button 
                  type="primary" 
                  icon={<CheckCircleOutlined />}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  onClick={() => handleUpdateAuditStatus(true)}
                >
                  确认审核
                </Button>
              )
            )}
            {taskInfo.status === 1 && (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRefreshStatus}
              >
                刷新状态
              </Button>
            )}
            <Button
              type="default" 
              icon={<ReloadOutlined />}
              onClick={() => {
                refreshMediaUrl(taskId)
                  .then(() => {
                    message.success('媒体URL已刷新');
                    fetchTaskDetail(); // 刷新获取最新数据
                  })
                  .catch(err => {
                    message.error('刷新媒体URL失败');
                    console.error(err);
                  });
              }}
            >
              刷新媒体链接
            </Button>
            {taskInfo.status === 3 && (
              <Button type="primary" onClick={handleRetry}>重新审核</Button>
            )}
          </Space>
        </div>

        {/* 处理中状态的提示 */}
        {taskInfo.status === 1 && (
          <Alert
            message="处理中的任务需要手动刷新获取最新状态"
            description="系统不会自动更新任务状态，请点击右上角「刷新状态」按钮获取最新处理进度。"
            type="info"
            showIcon
            icon={<ReloadOutlined />}
            style={{ marginBottom: '20px' }}
          />
        )}

        {/* 批处理进度条 */}
        {batchProgress.total > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <Alert 
              message={
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    处理进度: {batchProgress.current}/{batchProgress.total} 批 ({batchProgress.percent}%)
                  </div>
                  <Progress percent={batchProgress.percent} status={batchProgress.current === batchProgress.total ? "success" : "active"} />
                </div>
              } 
              type="info" 
            />
          </div>
        )}
        
        <Descriptions bordered column={8} size="middle" style={{ marginBottom: '20px' }}>
          <Descriptions.Item label="文件名" span={3}>{taskInfo.file_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="文件大小" span={1}>{taskInfo.file_size ? (taskInfo.file_size / 1024 / 1024).toFixed(2) + ' MB' : '-'}</Descriptions.Item>
                          <Descriptions.Item label="时长" span={1}>{taskInfo.duration ? formatDurationFromMs(taskInfo.duration) : '-'}</Descriptions.Item>
          <Descriptions.Item label="完成时间" span={3}>{taskInfo.complete_time ? new Date(taskInfo.complete_time).toLocaleString() : '-'}</Descriptions.Item>
        </Descriptions>

        {taskInfo.status === 2 && auditResult ? (
          <div>
            {taskInfo.is_video ? (
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  {/* AI内容检查结果摘要 - 放在视频播放上方 */}
                  <div className="ai-check-container">
                    {aiCheckResult ? (
                      <Card 
                        styles={{ body: { padding: '12px 16px' } }}
                        style={{ marginBottom: '16px', backgroundColor: '#f0f8ff' }}
                        variant={true ? 'outlined' : 'borderless'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                          <Text strong>AI内容检查</Text>
                          <div style={{ marginLeft: 'auto' }}>
                            {aiCheckResult.timeRelated && aiCheckResult.timeRelated.length > 0 ? (
                              <Tag color="orange" style={{ marginRight: '8px' }}>发现 {aiCheckResult.timeRelated.length} 处时间相关问题</Tag>
                            ) : (
                              <Tag color="success" style={{ marginRight: '8px' }}>未发现时间相关问题</Tag>
                            )}
                            {aiCheckResult.irrelevantContent && aiCheckResult.irrelevantContent.length > 0 ? (
                              <Tag color="red">发现 {aiCheckResult.irrelevantContent.length} 处教学无关内容</Tag>
                            ) : (
                              <Tag color="success">未发现教学无关内容</Tag>
                            )}
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <Card 
                        styles={{ body: { padding: '12px 16px' } }}
                        style={{ marginBottom: '16px', backgroundColor: '#f9f9f9' }}
                        variant={true ? 'outlined' : 'borderless'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                          <Text>AI内容检查</Text>
                          <Button 
                            type="primary" 
                            icon={<RobotOutlined />} 
                            onClick={showModelSelectModal}
                            loading={aiCheckLoading}
                            style={{ marginLeft: 'auto' }}
                            size="small"
                          >
                            开始检查
                          </Button>
                        </div>
                      </Card>
                    )}
                  </div>
                  <Card 
                    title="视频播放" 
                    styles={{ body: { height: 'calc(100vh - 455px)' } }}
                    style={{ marginBottom: '16px' }}
                    variant="outlined"
                  >
                    <video 
                      ref={videoRef}
                      src={taskInfo.video_url} 
                      style={{ width: '100%' }} 
                      controls={false}
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleMediaLoaded}
                      onPlay={() => setPlaying(true)}
                      onPause={() => setPlaying(false)}
                    />
                    <div style={styles.videoControls}>
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '8px' }}>
                        <Button 
                          type="text" 
                          icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />} 
                          onClick={handlePlayPause}
                          style={{ marginRight: '10px' }}
                        />
                        <div 
                          style={{ ...styles.progressBarContainer, flex: 1 }}
                          onClick={handleProgressClick}
                        >
                          <div 
                            style={{
                              ...styles.progressBar,
                              width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`
                            }} 
                          />
                        </div>
                      </div>
                      <div style={styles.timeDisplay}>
                        <span>{formatTimeSimple(currentTime)}</span>
                        <span>{formatTimeSimple(duration)}</span>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={16}>
                  {rawTranscriptionData && (
                    <Card style={{ height: 'calc(100vh - 400px)', overflow: 'auto' }} variant="outlined">
                      <div style={styles.chatTimelineContainer}>
                        {rawTranscriptionData.Transcription?.Paragraphs?.map((paragraph, idx) => {
                          const words = paragraph.Words || [];
                          if (words.length === 0) return null;
                          
                          const startTime = words[0].Start;
                          const endTime = words[words.length - 1].End;
                          const text = words.map(word => word.Text).join('');
                          const isCurrentSegment = currentTime >= startTime && currentTime <= endTime;
                          
                          // 查找本段落与下一段落之间是否有无文本时间段
                          const nextParagraph = rawTranscriptionData.Transcription?.Paragraphs[idx + 1];
                          const hasGap = nextParagraph && nextParagraph.Words && nextParagraph.Words.length > 0 && 
                                        (nextParagraph.Words[0].Start - endTime > 3000); // 3秒以上的间隔视为无文本时间段
                          
                          // 是否有AI检查标记
                          const timeRelatedIssue = aiCheckResult?.timeRelated?.find(
                            issue => text.includes(issue.text)
                          );
                          const irrelevantContentIssue = aiCheckResult?.irrelevantContent?.find(
                            issue => text.includes(issue.text)
                          );
                          
                          // 是否有AI检查标记
                          const hasAiIssue = !!timeRelatedIssue || !!irrelevantContentIssue;
                          
                          return (
                            <React.Fragment key={idx}>
                              <div style={{
                                ...styles.chatMessage,
                                ...(isCurrentSegment ? styles.chatMessageCurrent : {}),
                                ...(hasAiIssue ? { borderLeft: '3px solid #ff4d4f' } : {})
                              }}>
                                <div style={styles.chatMessageHeader}>
                                  <Tag color={paragraph.SpeakerId % 2 === 0 ? 'blue' : 'green'}>说话人 {paragraph.SpeakerId}</Tag>
                                  <span style={styles.chatTime}>{formatTimeSimple(startTime)} - {formatTimeSimple(endTime)}</span>
                                  <Button 
                                    type="link" 
                                    size="small" 
                                    icon={<PlayCircleOutlined />} 
                                    onClick={() => jumpToTime(startTime)}
                                  >
                                    播放
                                  </Button>
                                  {hasAiIssue && (
                                    <>
                                      {timeRelatedIssue && <Tag color="orange">时间相关问题</Tag>}
                                      {irrelevantContentIssue && <Tag color="red">教学无关内容</Tag>}
                                    </>
                                  )}
                                </div>
                                <div style={styles.chatMessageContent}>
                                  {text}
                                </div>
                                
                                {/* AI检查问题详情 */}
                                {hasAiIssue && (
                                  <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#fff2f0', borderRadius: '4px' }}>
                                    {timeRelatedIssue && (
                                      <div style={{ marginBottom: timeRelatedIssue && irrelevantContentIssue ? '8px' : '0' }}>
                                        <Text type="danger" strong>时间相关问题：</Text>
                                        <div>
                                          <Text mark>{timeRelatedIssue.text}</Text>
                                        </div>
                                        <div>
                                          <Text type="secondary">问题：{timeRelatedIssue.issue}</Text>
                                        </div>
                                        <div>
                                          <Text type="success">建议：{timeRelatedIssue.suggestion}</Text>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {irrelevantContentIssue && (
                                      <div>
                                        <Text type="danger" strong>教学无关内容：</Text>
                                        <div>
                                          <Text mark>{irrelevantContentIssue.text}</Text>
                                        </div>
                                        <div>
                                          <Text type="secondary">问题：{irrelevantContentIssue.issue}</Text>
                                        </div>
                                        <div>
                                          <Text type="success">建议：{irrelevantContentIssue.suggestion}</Text>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* 无文本时间段显示 */}
                              {hasGap && (
                                <div style={styles.chatGap}>
                                  <div style={styles.chatGapContent}>
                                    <InfoCircleOutlined /> 无人说话段落
                                    <span style={styles.chatTime}>{formatTimeSimple(endTime)} - {formatTimeSimple(nextParagraph.Words[0].Start)}</span>
                                    <Button 
                                      type="link" 
                                      size="small" 
                                      icon={<PlayCircleOutlined />} 
                                      onClick={() => jumpToTime(endTime)}
                                    >
                                      播放
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </Card>
                  )}
                </Col>
              </Row>
            ) : (
              // 音频模式
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  {/* AI内容检查结果摘要 - 放在音频播放上方 */}
                  <div className="ai-check-container">
                    {aiCheckResult ? (
                      <Card 
                        styles={{ body: { padding: '12px 16px' } }}
                        style={{ marginBottom: '16px', backgroundColor: '#f0f8ff' }}
                        variant={true ? 'outlined' : 'borderless'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                          <Text strong>AI内容检查</Text>
                          <div style={{ marginLeft: 'auto' }}>
                            {aiCheckResult.timeRelated && aiCheckResult.timeRelated.length > 0 ? (
                              <Tag color="orange" style={{ marginRight: '8px' }}>发现 {aiCheckResult.timeRelated.length} 处时间相关问题</Tag>
                            ) : (
                              <Tag color="success" style={{ marginRight: '8px' }}>未发现时间相关问题</Tag>
                            )}
                            {aiCheckResult.irrelevantContent && aiCheckResult.irrelevantContent.length > 0 ? (
                              <Tag color="red">发现 {aiCheckResult.irrelevantContent.length} 处教学无关内容</Tag>
                            ) : (
                              <Tag color="success">未发现教学无关内容</Tag>
                            )}
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <Card 
                        styles={{ body: { padding: '12px 16px' } }}
                        style={{ marginBottom: '16px', backgroundColor: '#f9f9f9' }}
                        variant={true ? 'outlined' : 'borderless'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                          <Text>AI内容检查</Text>
                          <Button 
                            type="primary" 
                            icon={<RobotOutlined />} 
                            onClick={showModelSelectModal}
                            loading={aiCheckLoading}
                            style={{ marginLeft: 'auto' }}
                            size="small"
                          >
                            开始检查
                          </Button>
                        </div>
                      </Card>
                    )}
                  </div>
                  <Card 
                    title="音频播放" 
                    styles={{ body: { height: 'calc(100vh - 455px)' } }}
                    style={{ marginBottom: '16px' }}
                    variant="outlined"
                  >
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', padding: '20px 0' }}>
                      <audio 
                        ref={audioRef}
                        src={taskInfo.video_url} 
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleMediaLoaded}
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        style={{ display: 'none' }}
                      />
                      <div style={{ width: '100%', maxWidth: '500px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '8px' }}>
                          <Button 
                            type="text" 
                            icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />} 
                            onClick={handlePlayPause}
                            style={{ marginRight: '10px' }}
                          />
                          <div 
                            style={{ ...styles.progressBarContainer, flex: 1 }}
                            onClick={handleProgressClick}
                          >
                            <div 
                              style={{
                                ...styles.progressBar,
                                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`
                              }} 
                            />
                          </div>
                        </div>
                        <div style={styles.timeDisplay}>
                          <span>{formatTimeSimple(currentTime)}</span>
                          <span>{formatTimeSimple(duration)}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={16}>
                  {rawTranscriptionData && (
                    <Card style={{ height: 'calc(100vh - 400px)', overflow: 'auto' }} variant="outlined">
                      <div style={styles.chatTimelineContainer}>
                        {rawTranscriptionData.Transcription?.Paragraphs?.map((paragraph, idx) => {
                          const words = paragraph.Words || [];
                          if (words.length === 0) return null;
                          
                          const startTime = words[0].Start;
                          const endTime = words[words.length - 1].End;
                          const text = words.map(word => word.Text).join('');
                          const isCurrentSegment = currentTime >= startTime && currentTime <= endTime;
                          
                          // 查找本段落与下一段落之间是否有无文本时间段
                          const nextParagraph = rawTranscriptionData.Transcription?.Paragraphs[idx + 1];
                          const hasGap = nextParagraph && nextParagraph.Words && nextParagraph.Words.length > 0 && 
                                        (nextParagraph.Words[0].Start - endTime > 3000); // 3秒以上的间隔视为无文本时间段
                          
                          // 是否有AI检查标记
                          const timeRelatedIssue = aiCheckResult?.timeRelated?.find(
                            issue => text.includes(issue.text)
                          );
                          const irrelevantContentIssue = aiCheckResult?.irrelevantContent?.find(
                            issue => text.includes(issue.text)
                          );
                          
                          // 是否有AI检查标记
                          const hasAiIssue = !!timeRelatedIssue || !!irrelevantContentIssue;
                          
                          return (
                            <React.Fragment key={idx}>
                              <div style={{
                                ...styles.chatMessage,
                                ...(isCurrentSegment ? styles.chatMessageCurrent : {}),
                                ...(hasAiIssue ? { borderLeft: '3px solid #ff4d4f' } : {})
                              }}>
                                <div style={styles.chatMessageHeader}>
                                  <Tag color={paragraph.SpeakerId % 2 === 0 ? 'blue' : 'green'}>说话人 {paragraph.SpeakerId}</Tag>
                                  <span style={styles.chatTime}>{formatTimeSimple(startTime)} - {formatTimeSimple(endTime)}</span>
                                  <Button 
                                    type="link" 
                                    size="small" 
                                    icon={<PlayCircleOutlined />} 
                                    onClick={() => jumpToTime(startTime)}
                                  >
                                    播放
                                  </Button>
                                  {hasAiIssue && (
                                    <>
                                      {timeRelatedIssue && <Tag color="orange">时间相关问题</Tag>}
                                      {irrelevantContentIssue && <Tag color="red">教学无关内容</Tag>}
                                    </>
                                  )}
                                </div>
                                <div style={styles.chatMessageContent}>
                                  {text}
                                </div>
                                
                                {/* AI检查问题详情 */}
                                {hasAiIssue && (
                                  <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#fff2f0', borderRadius: '4px' }}>
                                    {timeRelatedIssue && (
                                      <div style={{ marginBottom: timeRelatedIssue && irrelevantContentIssue ? '8px' : '0' }}>
                                        <Text type="danger" strong>时间相关问题：</Text>
                                        <div>
                                          <Text mark>{timeRelatedIssue.text}</Text>
                                        </div>
                                        <div>
                                          <Text type="secondary">问题：{timeRelatedIssue.issue}</Text>
                                        </div>
                                        <div>
                                          <Text type="success">建议：{timeRelatedIssue.suggestion}</Text>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {irrelevantContentIssue && (
                                      <div>
                                        <Text type="danger" strong>教学无关内容：</Text>
                                        <div>
                                          <Text mark>{irrelevantContentIssue.text}</Text>
                                        </div>
                                        <div>
                                          <Text type="secondary">问题：{irrelevantContentIssue.issue}</Text>
                                        </div>
                                        <div>
                                          <Text type="success">建议：{irrelevantContentIssue.suggestion}</Text>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* 无文本时间段显示 */}
                              {hasGap && (
                                <div style={styles.chatGap}>
                                  <div style={styles.chatGapContent}>
                                    <InfoCircleOutlined /> 无人说话段落
                                    <span style={styles.chatTime}>{formatTimeSimple(endTime)} - {formatTimeSimple(nextParagraph.Words[0].Start)}</span>
                                    <Button 
                                      type="link" 
                                      size="small" 
                                      icon={<PlayCircleOutlined />} 
                                      onClick={() => jumpToTime(endTime)}
                                    >
                                      播放
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </Card>
                  )}
                </Col>
              </Row>
            )}

            <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: '20px' }}
              items={[
                ...(auditResult?.translation ? [
                  {
                    key: "translation",
                    label: (
                      <span>
                        <FileTextOutlined />
                        翻译结果
                        <Button
                          type="link"
                          icon={<CopyOutlined />}
                          onClick={copyTranscriptionText}
                          style={{ marginLeft: 8 }}
                        >
                          复制全部
                        </Button>
                      </span>
                    ),
                    children: (
                      <Table 
                        columns={[
                          {
                            title: '原文',
                            dataIndex: 'original',
                            key: 'original',
                          },
                          {
                            title: '翻译',
                            dataIndex: 'translated',
                            key: 'translated',
                          },
                          {
                            title: '开始时间',
                            dataIndex: 'start',
                            key: 'start',
                            render: (start) => formatTime(start),
                          },
                          {
                            title: '结束时间',
                            dataIndex: 'end',
                            key: 'end',
                            render: (end, record) => formatTime(end),
                          },
                          {
                            title: '操作',
                            key: 'action',
                            render: (_, record) => (
                              <Button 
                                type="link" 
                                icon={<PlayCircleOutlined />} 
                                onClick={() => jumpToTime(record.start)}
                              >
                                播放
                              </Button>
                            ),
                          },
                        ]} 
                        dataSource={auditResult.translation.map((item, index) => ({ ...item, key: index }))} 
                        pagination={false}
                        scroll={{ y: 400 }}
                      />
                    )
                  }
                ] : []),
                ...(auditResult?.vad ? [
                  {
                    key: "vad",
                    label: (
                      <span>
                        <ClockCircleOutlined />
                        静音检测
                      </span>
                    ),
                    children: (
                      <Table 
                        columns={[
                          {
                            title: '开始时间',
                            dataIndex: 'start',
                            key: 'start',
                            render: (start) => formatTime(start),
                          },
                          {
                            title: '结束时间',
                            dataIndex: 'end',
                            key: 'end',
                            render: (end, record) => formatTime(end),
                          },
                          {
                            title: '持续时间',
                            dataIndex: 'duration',
                            key: 'duration',
                            render: (_, record) => formatDurationFromMs(record.end - record.start),
                          },
                          {
                            title: '操作',
                            key: 'action',
                            render: (_, record) => (
                              <Button 
                                type="link" 
                                icon={<PlayCircleOutlined />} 
                                onClick={() => jumpToTime(record.start)}
                              >
                                播放
                              </Button>
                            ),
                          },
                        ]} 
                        dataSource={auditResult.vad.map((item, index) => ({ ...item, key: index }))} 
                        pagination={false}
                        scroll={{ y: 400 }}
                      />
                    )
                  }
                ] : [])
              ]}
            />
            
            {taskInfo.ppt_extraction_url && (
              <div style={{ marginTop: '20px' }}>
                <PptExtractViewer 
                  url={taskInfo.ppt_extraction_url} 
                  items={[
                    {
                      key: '1',
                      label: 'PPT提取结果',
                      children: <div>PPT内容将在这里显示</div>
                    }
                  ]}
                />
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            {taskInfo.status === 0 || taskInfo.status === 1 ? (
              <div>
                <Spin />
                <Paragraph style={{ marginTop: '20px' }}>
                  任务正在处理中，请稍后刷新查看结果
                </Paragraph>
              </div>
            ) : (
              <div>
                <CloseCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
                <Paragraph style={{ marginTop: '20px' }}>
                  任务处理失败，请检查错误信息或重试
                </Paragraph>
              </div>
            )}
          </div>
        )}

        {/* 模型选择对话框 */}
        <Modal
          title="选择大模型"
          open={modelSelectVisible}
          onOk={handleModelSelect}
          onCancel={() => setModelSelectVisible(false)}
          okText="开始检查"
          cancelText="取消"
        >
          <div style={{ marginBottom: 16 }}>
            <p>请选择用于内容检查的大模型:</p>
            <Select
              style={{ width: '100%' }}
              value={selectedModel}
              onChange={(value) => setSelectedModel(value)}
              options={availableModels}
            />
          </div>
          <Alert
            message="提示"
            description={
              <div>
                <p>模型说明：</p>
                <ul>
                  <li>中等长度视频推荐使用智普GLM4-9B-128K大模型检查，处理速度较快；</li>
                  <li>长视频可以使用DeepSeek-V3(PRO)大模型检查，处理速度较慢，但准确率更高。</li>
                  <li>其他两个模型可以作为备用模型</li>
                </ul>
                <p>如果视频提取出来的文本内容超出单次处理能力，系统将自动分批处理并合并结果，以确保所有内容都得到分析。</p>
                <p>注意：检查结果只保存在本地缓存，刷新页面后需执行重新检查。</p>
              </div>
            }
            type="info"
            showIcon
          />
        </Modal>
      </Card>
    </div>
  );
};

export default TaskDetail; 